// Client layer (TS-2.5.03) - PM+ workflow, BRD section 8.5/D-182. Composes useDeckVersionDetail
// with the artifact-download, finalize and compare actions. The D-182 case this exists to handle
// honestly: a published version's artifacts may contain a pptx with no pdf yet (conversion is
// queued, or - per decisions.md D-204's 501 mapping - the converter is simply unavailable in this
// environment); this component never claims a PDF exists when it does not, and never treats a 501
// from re-export as "try again automatically."
import { useState } from "react";
import { ApiError } from "../../api/ApiError";
import { artifactsApi, saveBlob } from "../../api/artifacts";
import type { VersionDiff } from "../../api/generated/contracts";
import { publicationApi } from "../../api/publication";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { useDeckVersionDetail } from "./hooks/useDeckVersionDetail";
import { artifactOfFormat, formatDate, formatDateTime } from "./lib/deckFormat";
import { strings } from "../../strings";

export interface DeckVersionDetailProps {
  readonly versionKey: string;
  readonly canFinalize: boolean;
  readonly onBack: () => void;
}

const S = strings.decks.detail;

export function DeckVersionDetail({ versionKey, canFinalize, onBack }: DeckVersionDetailProps) {
  const { detail, loading, error, refresh } = useDeckVersionDetail(versionKey);

  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const [comparandKey, setComparandKey] = useState("");
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState<ApiError | null>(null);
  const [diff, setDiff] = useState<VersionDiff | null>(null);

  async function handleDownload(artifactKey: string, fileName: string) {
    setDownloadError(null);
    try {
      const blob = await artifactsApi.download(artifactKey);
      saveBlob(blob, fileName);
    } catch (cause) {
      setDownloadError(cause instanceof ApiError ? cause.message : "The artifact could not be downloaded.");
    }
  }

  async function handleGeneratePdf() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await publicationApi.reExport(versionKey, "Pdf");
      refresh();
    } catch (cause) {
      if (cause instanceof ApiError && cause.kind === "notImplemented") {
        setPdfError(S.pdfUnavailable);
      } else {
        setPdfError(cause instanceof ApiError ? cause.message : S.pdfError);
      }
    } finally {
      setPdfBusy(false);
    }
  }

  async function handleFinalize() {
    setFinalizing(true);
    try {
      await publicationApi.finalize(versionKey);
      refresh();
    } finally {
      setFinalizing(false);
    }
  }

  async function handleCompare() {
    if (comparandKey.trim().length === 0) return;
    setComparing(true);
    setCompareError(null);
    setDiff(null);
    try {
      const result = await publicationApi.compare(versionKey, comparandKey.trim());
      setDiff(result);
    } catch (cause) {
      setCompareError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The comparison could not be run."));
    } finally {
      setComparing(false);
    }
  }

  return (
    <section className="eg-deck-version-detail">
      <button type="button" onClick={onBack}>
        {S.back}
      </button>

      {loading && !detail && (
        <div role="status" aria-live="polite">
          {S.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{S.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {detail && (
        <>
          <h2>{detail.summary.title}</h2>
          <p>
            {formatDate(detail.summary.reportDate)} · {strings.decks.versions.states[detail.summary.state]}
          </p>
          <p>Data as of {formatDateTime(detail.summary.dataAsOfUtc)}</p>

          <h3>{S.artifacts}</h3>
          <ul>
            {detail.summary.artifacts.map((artifact) => (
              <li key={artifact.artifactKey}>
                {artifact.format} ({artifact.fileName})
                <button type="button" onClick={() => void handleDownload(artifact.artifactKey, artifact.fileName)}>
                  {S.download}
                </button>
              </li>
            ))}
          </ul>

          {downloadError && (
            <div role="alert">
              <p>{downloadError}</p>
            </div>
          )}

          {!artifactOfFormat(detail.summary.artifacts, "Pdf") && (
            <div className="eg-deck-version-detail__pdf-pending">
              <p>{S.pdfPending}</p>
              <button type="button" disabled={pdfBusy} onClick={() => void handleGeneratePdf()}>
                {pdfBusy ? S.pdfGenerating : S.pdfGenerate}
              </button>
              {pdfError && (
                <p role="alert">{pdfError}</p>
              )}
            </div>
          )}

          {canFinalize && detail.summary.state !== "Final" && (
            <div className="eg-deck-version-detail__finalize">
              <button type="button" disabled={finalizing} onClick={() => setFinalizeConfirmOpen(true)}>
                {finalizing ? S.finalizing : S.finalize}
              </button>
              <ConfirmDialog
                open={finalizeConfirmOpen}
                title={S.finalizeConfirmTitle}
                body={S.finalizeConfirmBody}
                confirmLabel={S.finalizeConfirmConfirm}
                cancelLabel={S.finalizeConfirmCancel}
                onConfirm={() => {
                  setFinalizeConfirmOpen(false);
                  void handleFinalize();
                }}
                onCancel={() => setFinalizeConfirmOpen(false)}
              />
            </div>
          )}

          <section className="eg-deck-version-detail__compare">
            <h3>{S.compareHeading}</h3>
            <FormField id="eg-deck-compare-key" label={S.compareLabel}>
              <input
                type="text"
                value={comparandKey}
                onChange={(event) => setComparandKey(event.target.value)}
                {...formFieldControlProps("eg-deck-compare-key")}
              />
            </FormField>
            <button type="button" disabled={comparing || comparandKey.trim().length === 0} onClick={() => void handleCompare()}>
              {S.compareSubmit}
            </button>

            {compareError && (
              <div role="alert">
                <p>{S.compareErrorTitle}</p>
                <p>{compareError.message}</p>
              </div>
            )}

            {diff && (
              <dl>
                <dt>Added</dt>
                <dd>{diff.summary.addedProjectCount}</dd>
                <dt>Removed</dt>
                <dd>{diff.summary.removedProjectCount}</dd>
                <dt>Changed</dt>
                <dd>{diff.summary.changedProjectCount}</dd>
              </dl>
            )}
          </section>
        </>
      )}
    </section>
  );
}
